/* Query and mutation hooks for Tasks — STORY-025 and STORY-026.
 *
 * The reader's timezone travels with every list request, because "overdue"
 * means the local day where they are standing — `glossary.md` owns that rule,
 * and the server cannot know it. It is part of the query key for the same
 * reason a filter is: a cached answer computed for another zone is a wrong
 * answer, not a stale one.
 */

import {
  type CreateTaskRequest,
  type ListTasksQuery,
  taskSchema,
  tasksResponseSchema,
  type UpdateTaskRequest,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';

export const tasksKey = (organisationId: string, query?: ListTasksQuery) =>
  query === undefined
    ? (['organisations', organisationId, 'tasks'] as const)
    : (['organisations', organisationId, 'tasks', query] as const);

const path = (organisationId: string) => `/organisations/${organisationId}/tasks`;

const toSearchParams = (query: ListTasksQuery): string => {
  const params = new URLSearchParams();
  if (query.assigneeId) params.set('assigneeId', query.assigneeId);
  /* Repeated, never joined with commas: the server reads a repeatable
   * parameter, and a comma-joined value would be one unrecognised status. */
  for (const status of query.status ?? []) params.append('status', status);
  for (const priority of query.priority ?? []) params.append('priority', priority);
  if (query.eventId) params.set('eventId', query.eventId);
  if (query.timeZone) params.set('timeZone', query.timeZone);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  const rendered = params.toString();
  return rendered === '' ? '' : `?${rendered}`;
};

export function useTasks(
  organisationId: string,
  query: ListTasksQuery = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    /* Not asked at all when the caller says there is nothing to ask for. An
     * empty status list means "none", and the server reads an absent one as
     * its default — so sending it would answer "show me nothing" with
     * everything. */
    enabled: options.enabled ?? true,
    queryKey: tasksKey(organisationId, query),
    queryFn: () =>
      request(`${path(organisationId)}${toSearchParams(query)}`, {
        schema: tasksResponseSchema,
      }),
    /* The previous page stays while the next loads, so paging and filtering do
     * not blank the list under the reader's hands. */
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/**
 * Every write invalidates the whole Task prefix.
 *
 * Not the page it was made from: a Task can move page the moment its status or
 * due date changes, since the list is sorted by overdue-then-due-date. Writing
 * one page back and trusting the rest would leave a completed Task sitting in a
 * list of unfinished ones.
 *
 * The hidden-overdue count moves with every write too, and that is the number
 * this screen exists to be honest about.
 */
const refresh = (queryClient: ReturnType<typeof useQueryClient>, organisationId: string) =>
  queryClient.invalidateQueries({ queryKey: tasksKey(organisationId) });

export function useCreateTask(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateTaskRequest) =>
      request(path(organisationId), { method: 'POST', schema: taskSchema, body: values }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

export function useUpdateTask(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, ...values }: UpdateTaskRequest & { taskId: string }) =>
      request(`${path(organisationId)}/${taskId}`, {
        method: 'PATCH',
        schema: taskSchema,
        body: values,
      }),
    onSuccess: () => refresh(queryClient, organisationId),
    /* And on FAILURE, which is the case that matters here. A 409 means the
     * Task moved underneath the reader, so the row on screen is showing a
     * value that is no longer true — refetching is how they see where it
     * actually got to rather than being told only that their save failed. */
    onError: () => refresh(queryClient, organisationId),
  });
}

export function useDeleteTask(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) =>
      request(`${path(organisationId)}/${taskId}`, { method: 'DELETE' }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}
